import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Col, Form, Input, Row } from "antd";
import PhoneInput from "antd-phone-input/legacy";
import { set } from "lodash";
import { formatPhoneNumber } from "@/utils/utils";
import { parsePhoneNumber } from "./utils";

const PhoneNumberInput = ({
  locationType = undefined,
  formItemProps: _formItemProps,
  showExtensionInput = true,
  borderRadius = 4,
  // onChange,
  disabled = false,
  nextInputRef = undefined,
  ...params
}) => {
  const [extensionValue, setExtensionValue] = useState("");
  const extensionInputRef = useRef();
  const [wasAutofilled, setWasAutofilled] = useState(false);

  const required = useMemo(() => {
    if (!_formItemProps) {
      return false;
    }

    const { rules } = _formItemProps || {};

    if (Array.isArray(rules)) {
      for (let i = rules.length - 1; i >= 0; i--) {
        // the last rule with "required": true rules this
        const rule = rules[i];

        if (rule.required) {
          return true;
        }
      }
    }

    return false;
  }, [_formItemProps]);

  const phoneInputName = useMemo(() => {
    const { name } = _formItemProps || {};

    if (locationType) {
      return [locationType, name];
    }

    return name;
  }, [locationType, _formItemProps]);

  const extensionInputName = useMemo(() => {
    const defaultName = "phoneExtension";

    if (!_formItemProps) {
      return defaultName;
    }

    const { name, extensionName } = _formItemProps || {};

    if (!name && !extensionName) {
      return defaultName;
    }

    const extensionNameNotNested = extensionName || `${name}Extension`;

    // If nesting level provided in form (e.g. "Pickup", "Dropoff", etc)
    if (locationType) {
      return [locationType, extensionNameNotNested];
    }

    return extensionNameNotNested;
  }, [_formItemProps, locationType]);

  const validator = useCallback(
    (_, { valid, countryCode, areaCode, phoneNumber, ...otherParams }) => {
      if (!required) {
        return Promise.resolve();
      }

      if (typeof valid === "function" && valid(true) && countryCode && areaCode && phoneNumber) {
        return Promise.resolve();
      }

      let newCountryCode = countryCode;
      let newAreaCode = areaCode;
      let newPhoneNumber = phoneNumber;

      if (typeof valid !== "function") {
        if (!newCountryCode || !newAreaCode || !newPhoneNumber) {
          let fromAutoFill = "";

          const keys = Object.keys(otherParams)
            .map(key => parseInt(key, 10))
            .sort((a, b) => a - b);

          keys?.forEach(key => {
            if (!Number.isNaN(key)) {
              fromAutoFill += otherParams[key];
            }
          });

          const parsed = parsePhoneNumber(fromAutoFill);
          newCountryCode = parsed.countryCode;
          newAreaCode = parsed.areaCode;
          newPhoneNumber = parsed.phoneNumber;
        }

        if (newCountryCode && newAreaCode && newPhoneNumber) {
          return Promise.resolve();
        }
      }

      const { label } = _formItemProps || {};

      return Promise.reject(countryCode || !label ? "Invalid phone number" : `Please enter ${label}`);
    },
    [_formItemProps, required],
  );

  const formItemProps = useMemo(() => {
    const newParams = { ..._formItemProps };
    let { rules } = newParams;

    if (!rules) {
      rules = [];
    }

    rules.push({ validator });
    newParams.rules = rules;

    return newParams;
  }, [validator, _formItemProps]);

  // Add default country code of 1 for pasted phone numbers
  useEffect(() => {
    const handlePaste = e => {
      const isTargetInput = e.target.parentNode.classList.contains(`${_formItemProps.name}-phone-input`);
      if (!isTargetInput) return;

      const pastedText = e.clipboardData.getData("text");
      const pastedTextCleaned = pastedText
        .trim()
        .replaceAll("(", "")
        .replaceAll(")", "")
        .replaceAll("+", "")
        .replaceAll(".", "")
        .replaceAll("-", "")
        .replaceAll(" ", "");

      let updatedInputValue;

      if (pastedTextCleaned.startsWith("1")) {
        updatedInputValue = `1 ${pastedTextCleaned.slice(1)}`;
      } else {
        updatedInputValue = `1 ${pastedTextCleaned}`;
      }

      const newValues = {};
      set(newValues, _formItemProps.name, updatedInputValue);
      _formItemProps?.formRef?.current?.setFieldsValue(newValues);

      e.target.value = formatPhoneNumber(updatedInputValue).replaceAll("-", " ");

      e.preventDefault();
    };

    window.addEventListener("paste", handlePaste);

    return () => {
      window.removeEventListener("paste", handlePaste);
    };
  }, [_formItemProps]);

  const handleKeyDown = e => {
    if (!nextInputRef) return;

    if (e.key === "Tab") {
      e.preventDefault();
      nextInputRef.current.focus();
    }
  };

  return (
    <>
      <Row wrap={false} style={{ width: "100%" }}>
        <Col span={showExtensionInput ? 19 : 24}>
          <Form.Item
            {...formItemProps}
            style={{
              flex: 1,
              ...formItemProps?.style,
            }}
            name={phoneInputName}
            label={<label style={{ maxHeight: "20px" }}>{formItemProps?.label}</label>}
          >
            {/* Chrome autofill does not work on this component directly due to having forced starting value matching user's country code */}
            <PhoneInput
              preferredCountries={["us", "ca", "mx"]}
              country="us"
              disabled={disabled}
              // When typing into an autofilled field, the blue styling will be removed
              onChange={() => setWasAutofilled(false)}
              className={`${showExtensionInput ? "tel-input-with-extension " : ""}${_formItemProps.name}-phone-input`}
              {...params}
              inputProps={{
                style: {
                  ...params?.style,
                  borderRadius,
                  borderTopRightRadius: showExtensionInput ? 0 : borderRadius,
                  borderBottomRightRadius: showExtensionInput ? 0 : borderRadius,
                  // When autofilled, chrome applies light blue styling internally. Add it here manually for uniform appearance
                  backgroundColor: wasAutofilled ? "#e8f0fe" : "white",
                },
                onKeyDown: e => {
                  // Ensure extension input is focused with one tab press
                  if (e.key === "Tab") {
                    e.preventDefault();
                    extensionInputRef.current.focus();
                  }
                },
                type: "tel",
              }}
            />
          </Form.Item>
        </Col>

        {showExtensionInput && (
          <Col span={5}>
            <Form.Item label={formItemProps?.extensionLabel ?? "Extension"} name={extensionInputName}>
              <Input
                disabled={disabled}
                ref={extensionInputRef}
                style={{
                  borderRadius,
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  borderLeftWidth: 0,
                  height: "32px",
                }}
                value={extensionValue}
                onChange={e => {
                  const digitsOnly = e.target.value.replace(/\D/g, "");
                  setExtensionValue(digitsOnly);
                  const newValues = {};
                  set(newValues, extensionInputName, digitsOnly);
                  _formItemProps?.formRef?.current?.setFieldsValue(newValues);
                }}
                autoComplete="nope" // Set to ensure chrome autofill ignores this input
                type="text" // Set to ensure chrome autofill does not mistake for intended phone input
                id="extensionInput" // Cannot have "phone" in it or it will break chrome autofill
                onKeyDown={handleKeyDown}
              />
            </Form.Item>
          </Col>
        )}
      </Row>
      {/* Hidden phone input is provided so that if chrome autofill occurs, it will fill this field and trigger updating the actual phone input above */}
      <input
        type="tel"
        name="phoneNumber"
        style={{
          position: "absolute",
          opacity: 0,
          height: 0,
          pointerEvents: "none",
        }}
        onChange={e => {
          // Detect Chrome. Autofill coloring and other functionalities might break in other browsers, so don't try.
          const isChrome = navigator.userAgent.indexOf("Chrome") > -1;

          if (!isChrome) return;

          const phone = e.target.value;

          if (!phone) return;

          const phoneValue = _formItemProps?.formRef?.current?.getFieldValue(phoneInputName);

          const phoneAlreadyHasInput = phoneValue?.areaCode || phoneValue?.phoneNumber;

          // Chrome autofill ignores fields with values already in them. Do same behavior for our manual autofill.
          if (phoneAlreadyHasInput) return;

          _formItemProps?.formRef?.current?.setFieldsValue({
            [phoneInputName]: `1 ${phone}`,
          });

          setWasAutofilled(true);
        }}
        autoComplete="tel"
      />
    </>
  );
};

PhoneNumberInput.defaultProps = {
  showExtensionInput: true,
};

export default PhoneNumberInput;
